import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Truck } from "lucide-react";
import { ShipFromFields, addressComplete } from "@/components/Marketplaces";
import { toast } from "sonner";

const FITS = ["/landing/fit_2.jpg", "/landing/fit_3.jpg", "/landing/fit_6.jpg"];

export default function AuthPage() {
  const { login, register, user, demoLogin } = useAuth();
  const [demoOpen, setDemoOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [hasAccounts, setHasAccounts] = useState(true);
  const [form, setForm] = useState({ email: "", password: "", handle: "", display_name: "" });
  const [shipFrom, setShipFrom] = useState({ name: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.authStatus().then((s) => { setHasAccounts(s.has_accounts); setDemoOpen(!!s.demo); if (!s.has_accounts) setMode("register"); }).catch(() => {});
  }, []);
  useEffect(() => { if (user) navigate(location.state?.from && location.state.from !== "/" ? location.state.from : "/home", { replace: true }); }, [user, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back");
      } else {
        if (!addressComplete(shipFrom)) { toast.error("Add your ship-from address so sold pieces get a label"); setBusy(false); return; }
        const r = await register({ email: form.email, password: form.password, handle: form.handle || undefined, display_name: form.display_name || undefined,
          ship_from: { ...shipFrom, state: shipFrom.state.toUpperCase(), name: shipFrom.name || form.display_name || form.handle } });
        toast.success(r.claimed_existing_closet ? "Account created — your existing closet is now yours" : "Welcome to ThreadList");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-dvh bg-background text-foreground grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="auth-page">
      {/* Form */}
      <div className="flex flex-col px-6 sm:px-10 py-6">
        <div className="flex items-center justify-between">
          <Brand />
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="auth-back-home"><ArrowLeft className="h-4 w-4" /> Back</Link>
        </div>
        <div className="flex-1 flex items-center justify-center py-10">
          <div className="w-full max-w-[400px] animate-rise">
            <div className="inline-flex rounded-full bg-secondary p-0.5 text-sm mb-8" role="tablist">
              {[["login", "Log in"], ["register", "Create account"]].map(([m, l]) => <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => setMode(m)} className={`px-4 h-8 rounded-full font-medium transition-colors ${mode === m ? "bg-background shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`auth-tab-${m}`}>{l}</button>)}
            </div>
            <h1 className="font-display text-[36px] sm:text-[44px] leading-[1.02]">{mode === "login" ? <>Welcome <em className="em">back.</em></> : <>Create your <em className="em">closet.</em></>}</h1>
            <p className="text-muted-foreground mt-3">{mode === "login" ? "Your closet, outfits and offers are waiting." : "Three minutes of setup, then every outfit renders on you."}</p>
            {!hasAccounts && mode === "register" && (
              <div className="mt-5 rounded-2xl border border-border bg-gray-25 p-3.5 text-sm" data-testid="auth-first-account-note">This is the first account — everything already in the closet becomes yours.</div>
            )}

            <form onSubmit={submit} className="mt-8 space-y-4">
              {mode === "register" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Handle</Label>
                    <Input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="yourname" className="mt-1.5 h-12 rounded-xl" data-testid="auth-handle-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Display name</Label>
                    <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Your Name" className="mt-1.5 h-12 rounded-xl" data-testid="auth-name-input" />
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-12 rounded-xl" data-testid="auth-email-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Password</Label>
                <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-12 rounded-xl" data-testid="auth-password-input" />
                {mode === "register" && <p className="text-[11px] text-muted-foreground mt-1.5">At least 6 characters.</p>}
              </div>
              {mode === "register" && (
                <div className="rounded-2xl border border-border bg-gray-25 p-3.5" data-testid="auth-shipfrom">
                  <p className="text-xs font-medium inline-flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Ship-from address</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5 mb-3">When a piece sells on eBay we buy the label and email it to you. This is the return address on it.</p>
                  <ShipFromFields value={shipFrom} onChange={setShipFrom} compact idPrefix="auth-shipfrom" />
                </div>
              )}
              <Button type="submit" disabled={busy} className="w-full rounded-full h-[52px] text-base" data-testid="auth-submit-button">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {mode === "login" ? "Log in" : "Create account"}
              </Button>
            </form>
            <p className="text-sm text-muted-foreground mt-6 text-center">
              {demoOpen && mode === "login" && <>Just looking? <button type="button" onClick={async () => { try { await demoLogin(); toast.success("You're in the demo closet"); } catch { toast.error("The demo isn't available right now"); } }} className="underline underline-offset-4 text-foreground" data-testid="auth-demo-button">Try the demo</button><span className="mx-2 text-muted-foreground/60">·</span></>}
              {mode === "login" ? "New here? " : "Already have an account? "}
              <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="underline underline-offset-4 text-foreground" data-testid="auth-switch-mode">
                {mode === "login" ? "Create your closet" : "Log in"}
              </button>
            </p>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">By continuing you agree to keep your closet photos your own. We never share them.</p>
      </div>

      {/* Editorial panel */}
      <div className="relative hidden lg:block bg-gray-25 border-l border-border overflow-hidden" aria-hidden="true">
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <div className="relative h-full">
            <div className="absolute left-0 top-4 w-[52%] aspect-[3/4] rounded-3xl overflow-hidden shadow-lift rotate-[-3deg]"><img src={FITS[0]} alt="" className="h-full w-full object-cover" /></div>
            <div className="absolute right-0 top-[18%] w-[46%] aspect-[3/4] rounded-3xl overflow-hidden shadow-lift rotate-[3deg]"><img src={FITS[1]} alt="" className="h-full w-full object-cover" /></div>
            <div className="absolute left-[22%] bottom-0 w-[44%] aspect-[3/4] rounded-3xl overflow-hidden shadow-lift rotate-[1deg]"><img src={FITS[2]} alt="" className="h-full w-full object-cover" /></div>
          </div>
          <p className="font-display text-[28px] leading-tight max-w-sm mt-8">Everything you own, <em className="em">styled on you,</em> worth something.</p>
        </div>
      </div>
    </div>
  );
}
